import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import {
  formatMediaLimitsIndication,
  MediaLimitsIndicationView,
} from '@app/modules/social-export/lib/format-media-limits-indication';
import { resolveMediaLimitsIndication } from '@app/modules/social-export/lib/resolve-media-limits-indication';
import orderBy from 'lodash/orderBy';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useStationConnections } from './useStationConnections';

/**
 * Resolves localized media-limits indications for each platform, keyed by platform id.
 *
 * Intended for compact selectors (publish dropdowns, quick-publish toggles) where the user
 * has not picked a connection yet. For each platform we evaluate limits from the station's
 * default connection for that platform — the same ordering as
 * {@link useStationPlatformConnections}: most recent `lastUsedForStationAt` first.
 *
 * When a connection is already selected (upload wizard), use {@link useMediaLimitsIndication}
 * with that connection instead so the Alert reflects the account that will actually publish.
 *
 * @param stationId - Station whose connections supply `mediaLimits`; undefined skips evaluation.
 * @param platforms - Platforms to include in the result map (typically those with ≥1 connection).
 * @param durationMs - Clip length in milliseconds. When undefined, only soft tips from the
 *   station-height tier can appear (no block / downscale without a known duration).
 * @returns Record of platform id → indication view-model (or undefined when publishing is ok).
 */
export function usePlatformMediaLimitsIndications(
  stationId: string | undefined,
  platforms: SocialPlatform[],
  durationMs: number | undefined,
): Record<string, MediaLimitsIndicationView | undefined> {
  const { $t } = useIntl();
  const { connections } = useStationConnections(stationId);

  return useMemo(() => {
    // API tiers express budgets in seconds; UI callers typically have ms (fragment chunks).
    const durationSec = durationMs != null ? durationMs / 1000 : undefined;
    const result: Record<string, MediaLimitsIndicationView | undefined> = {};

    for (const platform of platforms) {
      const forPlatform = connections.filter(
        (c) => c.platform.toUpperCase() === platform.id.toUpperCase(),
      );
      // Match useStationPlatformConnections: station-scoped recency; null → '' so never-used
      // connections sort last under desc (lodash otherwise puts null first).
      const connection = orderBy(forPlatform, [(c) => c.lastUsedForStationAt ?? ''], ['desc'])[0];
      const indication = resolveMediaLimitsIndication(connection?.mediaLimits, durationSec);
      result[platform.id] = formatMediaLimitsIndication(indication, $t, platform.name);
    }

    return result;
  }, [$t, connections, durationMs, platforms]);
}
